import { Box3, BoxGeometry, Group, Mesh, MeshBasicMaterial, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { createRackSelectionHighlight } from "./rack-shelf";

export const defaultPalletStackerCraneHeight = 8;
const controlCabinetBottomHeight = 1;

export type PalletStackerCraneOptions = {
  height?: number;
  length?: number;
  position?: { x?: number; y?: number; z?: number };
  rotationY?: number;
};

type AlignedPart = { object: Group; size: Vector3 };
type Parts = Record<
  "groundRail" | "base" | "post" | "top" | "skyRail" | "carriage" | "controlCabinet",
  AlignedPart
>;

const partAssets = {
  groundRail: ModelAsset.PalletStackerCraneGroundRail,
  base: ModelAsset.PalletStackerCraneBase,
  post: ModelAsset.PalletStackerCranePost,
  top: ModelAsset.PalletStackerCraneTop,
  skyRail: ModelAsset.PalletStackerCraneSkyRail,
  carriage: ModelAsset.PalletStackerCraneCarriage,
  controlCabinet: ModelAsset.PalletStackerCraneControlCabinet
} as const;

export class PalletStackerCrane extends Group {
  private height: number;
  private readonly length: number;
  private parts?: Parts;
  private readonly structure = new Group();
  private readonly hitTarget = createHitTarget();
  private readonly selectionHighlight = createRackSelectionHighlight();

  constructor({
    height = defaultPalletStackerCraneHeight,
    length = 10,
    position = {},
    rotationY = 0
  }: PalletStackerCraneOptions = {}) {
    super();
    this.height = validatePositiveNumber(height, "height");
    this.length = validatePositiveNumber(length, "length");
    if (!Number.isFinite(rotationY)) throw new RangeError("rotationY must be finite");
    this.name = "pallet-stacker-crane";
    this.structure.name = "pallet-stacker-crane-structure";
    this.hitTarget.name = "pallet-stacker-crane-hit-target";
    this.selectionHighlight.name = "pallet-stacker-crane-selection-highlight";
    this.selectionHighlight.visible = false;
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure, this.hitTarget, this.selectionHighlight);
  }

  static async create(
    options: PalletStackerCraneOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const crane = new PalletStackerCrane(options);
    const entries = await Promise.all(
      Object.entries(partAssets).map(async ([key, asset]) => {
        const model = await loader.load(asset);
        model.updateWorldMatrix(true, true);
        const bounds = new Box3().setFromObject(model);
        const size = bounds.getSize(new Vector3());
        if (bounds.isEmpty() || size.x <= 0 || size.y <= 0 || size.z <= 0) {
          throw new Error(`${asset} model has no usable bounds`);
        }
        const center = bounds.getCenter(new Vector3());
        model.position.sub(new Vector3(center.x, bounds.min.y, center.z));
        const object = new Group();
        object.name = asset;
        object.add(model);
        return [key, { object, size }] as const;
      })
    );
    crane.parts = Object.fromEntries(entries) as Parts;
    crane.structure.add(...entries.map(([, part]) => part.object));
    crane.layoutStructure();
    return crane;
  }

  getHeight() {
    return this.height;
  }

  getHitTargets(): Object3D[] {
    return [this.hitTarget];
  }

  setHeight(height: number) {
    this.height = validatePositiveNumber(height, "height");
    this.layoutStructure();
  }

  setSelected(selected: boolean) {
    this.selectionHighlight.visible = selected;
  }

  private layoutStructure() {
    if (!this.parts) return;
    const { groundRail, base, post, top, skyRail, carriage, controlCabinet } = this.parts;
    groundRail.object.scale.x = this.length / groundRail.size.x;
    skyRail.object.scale.x = this.length / skyRail.size.x;
    base.object.position.y = groundRail.size.y;
    const postBottom = groundRail.size.y + base.size.y;
    post.object.position.y = postBottom;
    post.object.scale.y = this.height / post.size.y;
    top.object.position.y = postBottom + this.height;
    skyRail.object.position.y = top.object.position.y + top.size.y;
    carriage.object.position.y = postBottom + this.height * 0.36;
    controlCabinet.object.position.set(
      -(post.size.x + controlCabinet.size.x) / 2,
      controlCabinetBottomHeight,
      0
    );

    const totalHeight = Math.max(
      skyRail.object.position.y + skyRail.size.y,
      carriage.object.position.y + carriage.size.y,
      controlCabinetBottomHeight + controlCabinet.size.y
    );
    const totalLength = Math.max(
      this.length,
      base.size.x,
      post.size.x,
      top.size.x,
      carriage.size.x
    );
    const totalWidth = Math.max(...Object.values(this.parts).map((part) => part.size.z));
    const minX = Math.min(
      -totalLength / 2,
      controlCabinet.object.position.x - controlCabinet.size.x / 2
    );
    const maxX = totalLength / 2;
    this.hitTarget.position.set((minX + maxX) / 2, totalHeight / 2, 0);
    this.hitTarget.scale.set(maxX - minX, totalHeight, totalWidth);
    this.selectionHighlight.position.copy(this.hitTarget.position);
    this.selectionHighlight.scale.copy(this.hitTarget.scale).addScalar(0.12);
  }
}

function createHitTarget() {
  const material = new MeshBasicMaterial({ depthWrite: false, opacity: 0, transparent: true });
  material.colorWrite = false;
  return new Mesh(new BoxGeometry(1, 1, 1), material);
}

function validatePositiveNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value <= 0)
    throw new RangeError(`${label} must be a positive finite number`);
  return value;
}
